import React, { useCallback } from 'react';
import CreateVerificationModal from './CreateVerificationModal';
import EditVerificationModal from './EditVerificationModal';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import isVerifier from './isVerifier';
import { PostPolicyAction, usePostPolicyCheck } from './postPolicyCheck';
import usePostVerifyNavigation from './usePostVerifyNavigation';
import VerifyPostModal from './VerifyPostModal';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { MenuItem } from '../components/Menu';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import editIcon from '../icons/non-prefilled/16x16/editPost.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verified.svg';
import { UIActions } from '../ui/uiSlice';

const POST_FRAGMENT = gql(`
  fragment VerifyMenuItems_post on Post {
    verification {
      id
    }
    archivedAt
    ...usePostPolicyCheck_post
    ...VerifyPostModal_post
    ...EditVerificationModal_post
    ...getVerificationStatus_post
    ...isVerifier_post
  }
`);

const USE_SHOULD_RENDER_VERIFY_MENU_ITEMS_POST_FRAGMENT = gql(`
  fragment useShouldRenderVerifyMenuItems_post on Post {
    id
    ...VerifyMenuItems_post
  }
  `);

export const useShouldRenderVerifyMenuItems = () => {
  const postPolicyCheck = usePostPolicyCheck();

  return useCallback(
    ({
      post,
      user,
    }: {
      post: DocumentType<
        typeof USE_SHOULD_RENDER_VERIFY_MENU_ITEMS_POST_FRAGMENT
      >;
      user: { id: string };
    }) => {
      return (
        !!post.publishedAt &&
        !post.archivedAt &&
        (postPolicyCheck(PostPolicyAction.EditPost, post, user) ||
          isVerifier(post, user))
      );
    },
    [postPolicyCheck],
  );
};

interface VerifyMenuItemsProps {
  post: DocumentType<typeof POST_FRAGMENT>;
}

const VerifyMenuItems = ({ post }: VerifyMenuItemsProps) => {
  const user = useAppSelector((state) => state.session.user);
  const dispatch = useAppDispatch();
  const postPolicyCheck = usePostPolicyCheck();
  const { postVerifyNavigate, loading } = usePostVerifyNavigation(post.id);

  if (post.verification) {
    return (
      <>
        {isVerifier(post, user) &&
          (getVerificationStatus(post) === VerificationStatus.Requested ? (
            <MenuItem
              icon={verifiedIcon}
              onClick={() =>
                dispatch(UIActions.openModal(VerifyPostModal, { post }))
              }
            >
              Verify post
            </MenuItem>
          ) : (
            <MenuItem
              disabled={loading}
              icon={verifiedIcon}
              onClick={() => postVerifyNavigate()}
            >
              Reverify post
            </MenuItem>
          ))}
        {postPolicyCheck(PostPolicyAction.EditPost, post, user) && (
          <MenuItem
            icon={editIcon}
            onClick={() =>
              dispatch(
                UIActions.openModal(EditVerificationModal, {
                  post,
                }),
              )
            }
          >
            Edit verification
          </MenuItem>
        )}
      </>
    );
  }

  const isUserAllowedToVerifyPost = isVerifier(post, user);

  return postPolicyCheck(PostPolicyAction.EditPost, post, user) ||
    isUserAllowedToVerifyPost ? (
    <MenuItem
      icon={verifiedIcon}
      onClick={() =>
        dispatch(UIActions.openModal(CreateVerificationModal, { post }))
      }
    >
      Verify
    </MenuItem>
  ) : null;
};

export default VerifyMenuItems;
